import { Add, ArrowDownwardOutlined, ArrowUpwardOutlined, DeleteOutlined, EditOutlined, TableChartOutlined, Visibility } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import usePagebuilder from '../../../hooks/pagebuilder/usePagebuilder';
import { Network } from '../../../interfaces/interfaces';
import { AgendaComponentInterface, UiComponent } from '../../../interfaces/pagebuilder';
import {
  setIsPBBoothsModal,
  setIsPBMatchingModal,
  setIsPBPresentationsModal,
  setNetworks,
  setPBAddSpeakerComponent,
  setPBSpeakersModalComponent,
  setShowBoothOnboardModal,
} from '../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { VyConfirm } from '../../common/VyConfirm';
import VyPrivilegeWrapper from '../../common/VyPrivilegeWrapper';
import VyTooltip from '../../common/VyTooltip';
import TemplateControlMenu from './TemplateControlMenu';
import UiTemplateAgendaControl from './UiTemplateAgendaControl';
export interface UiTemplateControlInterface {
  firstComponent: boolean;
  lastComponent: boolean;
  component: UiComponent;
  isVisible: boolean;
  isFullWidth: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onMoveOut: (pageId: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onAdd: () => void;
  onToggle: () => void;
  onCopy: (pageid: string) => void;
  onVisibility: () => void;
  updateShowControl: (showControl: boolean) => void;
  children?: ReactNode;
}

const UiTemplateControl = (props: UiTemplateControlInterface) => {
  const {
    firstComponent,
    component,
    lastComponent,
    isVisible,
    isFullWidth = false,
    children,
    onMoveUp,
    onMoveDown,
    onDelete,
    onMoveOut,
    onCopy,
    onEdit,
    onAdd,
    onToggle,
    onVisibility,
    updateShowControl,
  } = props;
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const networks = useSelector((state) => state.common.networks);

  const { t } = useTranslation('pagebuilder');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { showControlByType } = usePagebuilder();
  const dispatch = useDispatch();

  const [showUI, setShowUI] = useState(templateEditMode);
  const [animateShow, setAnimateShow] = useState(false);
  const [showControl, setShowControl] = useState<boolean>(false);

  const createBlankNetworkingRoom = () => {
    const newNetworkRoom: Partial<Network> = {
      _id: 'blank',
      subtitle: '',
      subtext: '',
      name: '',
      description: '',
      image: undefined,
      bannerImage: undefined,
      settings: {
        userlimit: 0,
        visible: true,
        joinwithoutadmin: true,
        usermustknock: false,
      },
    };
    dispatch(setNetworks([...networks, newNetworkRoom as Network]));
  };

  function decideVisibilityTooltip(): string {
    if (component.isRestricted) {
      return t('component-is-restricted');
    } else if (component.accessType !== 'anyone') {
      return t(`component-visible-for-${component.accessType}`);
    } else return '';
  }

  useEffect(() => {
    //return setShowUI(templateEditMode);

    if (!templateEditMode) {
      /**
       * we let the animation hide the conrtols
       * only then we change display to none
       */
      setAnimateShow(false);
      setTimeout(() => {
        setShowUI(false);
      }, 300);
    } else {
      setShowUI(true);
      /**
       * timeout for display to change from none to flex, only then the animation is triggered
       * otherwise the animation will look buggy
       */
      setTimeout(() => {
        setAnimateShow(true);
      }, 50);
    }
  }, [templateEditMode]);

  useEffect(() => updateShowControl(showControl), [showControl]);

  return (
    <div className="w-full" onMouseEnter={() => setShowControl(true)} onMouseLeave={() => setShowControl(false)}>
      <div
        className={`flex flex-row  w-full p-0 opacity-${showControl && showUI ? 1 : 0} ${showUI ? 'flex' : 'hidden'} h-${
          animateShow ? '8' : '0'
        } justify-end transition-all`}>
        <div className="flex flex-row items-center h-full rounded-t-lg bg-vystemblue">
          <TemplateControlMenu isVisible={isVisible} onCopy={onCopy} onMoveOut={onMoveOut} onToggle={onToggle} />
          {showControlByType(component, ['agenda']) && <UiTemplateAgendaControl component={component as AgendaComponentInterface} />}
          {showControlByType(component, ['speakers']) && (
            <VyTooltip title={t('pagebuilder:show-all-speakers')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={() => dispatch(setPBSpeakersModalComponent(component))}>
                <TableChartOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['booths', 'infobooth']) && (
            <VyTooltip title={t('pagebuilder:create-new-booth')}>
              <Button
                onClick={() => dispatch(setShowBoothOnboardModal(true))}
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation>
                <Add fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['booths', 'infobooth']) && (
            <VyTooltip title={t('pagebuilder:show-all-exhibitors')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={() => dispatch(setIsPBBoothsModal(true))}>
                <TableChartOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['presentation', 'presentationpreviewlist']) && (
            <VyTooltip title={t('pagebuilder:show-all-presentations')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={() => dispatch(setIsPBPresentationsModal(true))}>
                <TableChartOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['network']) && (
            <VyTooltip title={t('pagebuilder:networking-add')}>
              <Button
                onClick={() => createBlankNetworkingRoom()}
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disabled={!!networks.find((n) => n._id === 'blank')}
                disableElevation>
                <Add fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['speakers']) && (
            <VyTooltip title={t('pagebuilder:add-new-entry')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={() => dispatch(setPBAddSpeakerComponent(component))}>
                <Add fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {showControlByType(component, ['speeddating']) && (
            <VyTooltip title={t('pagebuilder:show-matching-info')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={() => dispatch(setIsPBMatchingModal(true))}>
                <TableChartOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          <VyPrivilegeWrapper privilege="pagebuilder">
            {(access) => (
              <VyTooltip title={access ? t('edit-component-title') : ''}>
                <Button
                  disabled={!access}
                  size="small"
                  variant="contained"
                  sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                  disableElevation
                  onClick={onEdit}>
                  <EditOutlined fontSize="small" />
                </Button>
              </VyTooltip>
            )}
          </VyPrivilegeWrapper>
          {!firstComponent && (
            <VyTooltip title={t('sort-component-title')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={onMoveUp}>
                <ArrowUpwardOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {!lastComponent && (
            <VyTooltip title={t('sort-component-title')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={onMoveDown}>
                <ArrowDownwardOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {component.accessType !== 'anyone' && (
            <VyPrivilegeWrapper privilege="pagebuilder">
              {(access) => (
                <VyTooltip title={access ? decideVisibilityTooltip() : ''}>
                  <Button
                    className="animate-pulse"
                    onClick={onVisibility}
                    disabled={!access}
                    size="small"
                    variant="contained"
                    sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                    disableElevation>
                    <Visibility fontSize="small" />
                  </Button>
                </VyTooltip>
              )}
            </VyPrivilegeWrapper>
          )}
          <VyPrivilegeWrapper privilege="pagebuilder">
            {(access) => (
              <VyConfirm disabled={!access} onConfirm={onDelete}>
                <VyTooltip title={access ? t('delete-component-title') : ''}>
                  <Button
                    disabled={!access}
                    size="small"
                    variant="contained"
                    sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                    disableElevation>
                    <DeleteOutlined fontSize="small" />
                  </Button>
                </VyTooltip>
              </VyConfirm>
            )}
          </VyPrivilegeWrapper>
        </div>
      </div>
      {children}
      <div
        onClick={onAdd}
        className={`w-full h-${animateShow ? '4' : '0'} justify-center relative p-0 opacity-${showControl && showUI ? 1 : 0} ${
          showUI ? 'flex' : 'none'
        } transition-all`}>
        <VyPrivilegeWrapper privilege="pagebuilder" hideIfDenied>
          {(access) => (
            <Button disabled={!access} onClick={onAdd} className="outline-none" variant="contained" color="secondary">
              <p style={{ fontSize: '11px', fontWeight: 'bold' }}>{t('pagebuilder:add-new-component')}</p>
            </Button>
          )}
        </VyPrivilegeWrapper>
      </div>
    </div>
  );
};

export default UiTemplateControl;
